/**
 * `FoundryDocTools` (`header.pills.refresh`, order 76): on a document page, the refresh pill
 * (stage s2, deliverable 16) and the biography runner when a pending entry names this document
 * (stage s1). The pill is a link, "Refresh from Foundry" to `/foundry?sheet=<sheetId>`, on a live
 * canon document whose character sheet came from Foundry (DECISIONS 224 ruling 1). The runner needs
 * the editor, so it is loaded by `import()` only once an entry is pending (the entry graph reaches
 * no editor module, `compose/entry-boundary.test.ts`).
 */
import type { CharacterSheetV1, SlotComponentProps } from '@codex/contracts';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useCharacterDoc, useCharacterSheets } from '../../sheets/pc/data/hooks.js';
import { useGoTo } from '../../shell/open-doc.js';
import { useSheetsEnabled } from '../entry.js';
import { foundryHref } from '../href.js';
import { PILL_TEXT } from '../model/copy.js';
import { type PendingBiography, takePendingBiography, usePendingBiography } from './pending.js';

const BiographyRunner = lazy(() => import('./runner.js'));

export function FoundryDocTools({ docId }: SlotComponentProps) {
  return useSheetsEnabled() ? <DocTools docId={docId} /> : null;
}

/** The sheet the pill refreshes: the primary when Foundry's, else the first from Foundry. */
export function foundrySheetOf(sheets: readonly CharacterSheetV1[]): CharacterSheetV1 | null {
  const fromFoundry = sheets.filter((sheet) => sheet.foundry !== null);
  return fromFoundry.find((sheet) => sheet.isPrimary) ?? fromFoundry[0] ?? null;
}

function RefreshPill({ docId }: { docId: string }) {
  const goTo = useGoTo();
  const cd = useCharacterDoc(docId, true);
  const live = cd.sheeted && cd.doc?.deletedAt === null;
  const sheets = useCharacterSheets(docId, live);
  const sheet = live && sheets.data ? foundrySheetOf(sheets.data) : null;
  if (!sheet) return null;
  const href = foundryHref({ sheet: sheet.id });
  return (
    <a
      className="cx-button cx-button--secondary cx-button--sm"
      href={href}
      data-testid="foundry.refresh.pill"
      onClick={(e) => goTo(href, e)}
    >
      <span className="cx-button__label">{PILL_TEXT.pill}</span>
    </a>
  );
}

function DocTools({ docId }: { docId: string }) {
  const pending = usePendingBiography(docId);
  const [taken, setTaken] = useState<PendingBiography | null>(null);
  useEffect(() => {
    const entry = pending ? takePendingBiography(docId) : null;
    if (entry) setTaken(entry);
  }, [pending, docId]);
  return (
    <>
      <RefreshPill docId={docId} />
      {taken && (
        <Suspense fallback={null}>
          <BiographyRunner key={taken.importId} entry={taken} onDone={() => setTaken(null)} />
        </Suspense>
      )}
    </>
  );
}
